import { seriesColorAt } from './constants';
import type { MetricSeriesPoint, MetricSeriesSpec } from './seriesTypes';

/**
 * Builders that turn rows into chart series.
 *
 * The x-axis helpers they pair with live in `dayAxis.ts`: deciding which days an axis spans
 * is a separate job from shaping a series, and keeping both here pushed one file past the
 * size the repo asks for.
 */

/** Normalises an extracted metric into a series point value. */
function toSeriesValue(value: number | null | undefined): number | null {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return null;
    }
    return value;
}

/** Maps rows to day-keyed points, dropping rows with no day. */
export function toSeriesPoints<TRow>(
    rows: TRow[],
    day: (row: TRow) => string,
    value: (row: TRow) => number | null | undefined
): MetricSeriesPoint[] {
    const points: MetricSeriesPoint[] = [];

    for (const row of rows) {
        const rowDay = day(row);
        if (!rowDay) {
            continue;
        }
        points.push({ day: rowDay, value: toSeriesValue(value(row)) });
    }

    return points;
}

interface SeriesPerDaypartParams<TRow> {
    rows: TRow[];
    /** The daypart tags to render, in the order they should appear. */
    daypartTags: string[];
    day: (row: TRow) => string;
    daypartTag: (row: TRow) => string;
    value: (row: TRow) => number | null | undefined;
    /** Already-translated label for a daypart tag. */
    label: (daypartTag: string) => string;
    /** Defaults to the shared palette, assigned in `daypartTags` order. */
    color?: (daypartTag: string, index: number) => string;
    axis?: 'left' | 'right';
    /** Prefix for the series `key`, so several metrics can share one chart. */
    keyPrefix?: string;
}

/**
 * Fans one metric out into a series per daypart tag.
 *
 * Used by the charts that plot the same metric for `day` / `night` / `all` etc.
 * Tags with no matching rows still produce a series, so the legend stays stable
 * as the date range changes.
 */
export function seriesPerDaypart<TRow>(params: SeriesPerDaypartParams<TRow>): MetricSeriesSpec[] {
    const {
        rows,
        daypartTags,
        day,
        daypartTag,
        value,
        label,
        color = (_tag, index) => seriesColorAt(index),
        axis,
        keyPrefix = 'daypart',
    } = params;

    return daypartTags.map((tag, index) => ({
        key: `${keyPrefix}:${tag}`,
        label: label(tag),
        color: color(tag, index),
        axis,
        data: toSeriesPoints(
            rows.filter((row) => daypartTag(row) === tag),
            day,
            value
        ),
    }));
}

interface TrendSeriesParams<TRow> {
    rows: TRow[];
    day: (row: TRow) => string;
    value: (row: TRow) => number | null | undefined;
    /** Already translated. */
    label: string;
    color: string;
    key: string;
    axis?: 'left' | 'right';
    /** Defaults to `true`. Set `false` where the trend reads as a solid line. */
    dashed?: boolean;
    /**
     * Defaults to `false` — a trend derived from a series already on the chart is
     * usually not worth toggling separately. Charts whose trend is the primary
     * line, with the measurements shown as points beside it, set this to `true`.
     */
    showInLegend?: boolean;
}

/**
 * Builds a companion series — a fitted trend plotted alongside measured values. It stays
 * out of the chart's CSV download, which holds the measured values only.
 */
export function trendSeries<TRow>(params: TrendSeriesParams<TRow>): MetricSeriesSpec {
    const {
        rows,
        day,
        value,
        label,
        color,
        key,
        axis,
        dashed = true,
        showInLegend = false,
    } = params;

    return {
        key,
        label,
        color,
        axis,
        dashed,
        showInLegend,
        excludeFromCsv: true,
        data: toSeriesPoints(rows, day, value),
    };
}
